import Link from "next/link";
import {Balancer} from "react-wrap-balancer";
import {scrollToElement} from "@/utils/scrollToElement";

type CloudVmDataType = {
  heading: string;
  data: {
    cpu: string;
    memory: string;
    storage: string;
    bandwidth: string;
    monthlyPrice: number;
  }[];
  footer?: {
    btnText?: string;
    text1?: string | React.ReactNode;
  };
};

export const CLOUD_VM_INTEL_DEDICATED: CloudVmDataType = {
  heading: "Cloud VM - Intel Based Dedicated CPU (General Purpose) TIER1",
  data: [
    {
      cpu: "2",
      memory: "4GB",
      storage: "80GB",
      bandwidth: "80",
      monthlyPrice: 25.0,
    },
    {
      cpu: "4",
      memory: "8GB",
      storage: "160GB",
      bandwidth: "160",
      monthlyPrice: 50.0,
    },
    {
      cpu: "8",
      memory: "16GB",
      storage: "320GB",
      bandwidth: "320",
      monthlyPrice: 100.0,
    },
    {
      cpu: "16",
      memory: "32GB",
      storage: "640GB",
      bandwidth: "640",
      monthlyPrice: 200.0,
    },
    {
      cpu: "32",
      memory: "64GB",
      storage: "1280GB",
      bandwidth: "1280",
      monthlyPrice: 400.0,
    },
    {
      cpu: "48",
      memory: "96GB",
      storage: "1920GB",
      bandwidth: "1920",
      monthlyPrice: 600.0,
    },
  ],
  footer: {
    text1: (
      <>
        Note : Intel Compute is based on Intel Scalable Architecture.
        <br />
        <Balancer>
          We offer a wide range of Cloud VM configurations to suit your workloads. The
          prices listed are indicative and may fluctuate based on resource availability
          and billing cycles. Reach out to our{" "}
          <Link
            href="#contact-us"
            onClick={(e) => {
              e.preventDefault();
              scrollToElement("contact-us", 120);
            }}
            target="_blank"
            style={{textDecoration: "underline", color: "#1E87F0", fontWeight: 700}}
          >
            sales team
          </Link>{" "}
          for customized plans or volume monthlyPrice.
        </Balancer>
      </>
    ),
    btnText: "Get a Custom Quote.",
  },
};

export const CLOUD_VM_AMD_DEDICATED: CloudVmDataType = {
  heading: "Cloud VM  - AMD Based Dedicated CPU (General Purpose)",
  data: [
    {
      cpu: "1",
      memory: "4GB",
      storage: "30GB",
      bandwidth: "4.00TB",
      monthlyPrice: 30.0,
    },
    {
      cpu: "2",
      memory: "8GB",
      storage: "50GB",
      bandwidth: "5.00TB",
      monthlyPrice: 60.0,
    },
    {
      cpu: "4",
      memory: "16GB",
      storage: "80GB",
      bandwidth: "7.00TB",
      monthlyPrice: 120.0,
    },
    {
      cpu: "8",
      memory: "32GB",
      storage: "160GB",
      bandwidth: "8.00TB",
      monthlyPrice: 240.0,
    },
    {
      cpu: "16",
      memory: "64GB",
      storage: "320GB",
      bandwidth: "8.00TB",
      monthlyPrice: 480.0,
    },
    {
      cpu: "24",
      memory: "96GB",
      storage: "480GB",
      bandwidth: "9.00TB",
      monthlyPrice: 720.0,
    },
    {
      cpu: "32",
      memory: "128GB",
      storage: "640GB",
      bandwidth: "9.00TB",
      monthlyPrice: 960.0,
    },
    {
      cpu: "40",
      memory: "160GB",
      storage: "768GB",
      bandwidth: "10.00TB",
      monthlyPrice: 1200.0,
    },
    {
      cpu: "64",
      memory: "192GB",
      storage: "960GB",
      bandwidth: "11.00TB",
      monthlyPrice: 1920.0,
    },
    {
      cpu: "96",
      memory: "256GB",
      storage: "1280GB",
      bandwidth: "12.00TB",
      monthlyPrice: 3840.0,
    },
  ],
  footer: {
    text1: (
      <>
        Note : AMD is GEN3 and Gen4.
        <br />
        <Balancer>
          We offer a wide range of Cloud VM configurations to suit your workloads. The
          prices listed are indicative and may fluctuate based on resource availability
          and billing cycles. Reach out to our{" "}
          <Link
            href="#contact-us"
            onClick={(e) => {
              e.preventDefault();
              scrollToElement("contact-us", 120);
            }}
            target="_blank"
            style={{textDecoration: "underline", color: "#1E87F0", fontWeight: 700}}
          >
            sales team
          </Link>{" "}
          for customized plans or volume monthlyPrice.
        </Balancer>
      </>
    ),
    btnText: "Get a Custom Quote.",
  },
};

export const CLOUD_VM_SHARED_INTEL: CloudVmDataType = {
  heading: "Cloud VM - Shared (Older Gen Intel Based)",
  data: [
    {
      cpu: "1",
      memory: "0.5GB",
      storage: "10GB",
      bandwidth: "0.50TB",
      monthlyPrice: 2.5,
    },
    {
      cpu: "1",
      memory: "0.5GB",
      storage: "10GB",
      bandwidth: "0.50TB",
      monthlyPrice: 3.5,
    },
    {
      cpu: "1",
      memory: "1GB",
      storage: "25GB",
      bandwidth: "1.00TB",
      monthlyPrice: 5.0,
    },
    {
      cpu: "2",
      memory: "2GB",
      storage: "50GB",
      bandwidth: "2.00TB",
      monthlyPrice: 9.0,
    },
    {
      cpu: "2",
      memory: "3GB",
      storage: "60GB",
      bandwidth: "3.00TB",
      monthlyPrice: 15.0,
    },
    {
      cpu: "2",
      memory: "4GB",
      storage: "80GB",
      bandwidth: "3.00TB",
      monthlyPrice: 20.0,
    },
    {
      cpu: "4",
      memory: "8GB",
      storage: "160GB",
      bandwidth: "4.00TB",
      monthlyPrice: 40.0,
    },
    {
      cpu: "6",
      memory: "16GB",
      storage: "320GB",
      bandwidth: "5.00TB",
      monthlyPrice: 80.0,
    },
    {
      cpu: "8",
      memory: "32GB",
      storage: "640GB",
      bandwidth: "6.00TB",
      monthlyPrice: 160.0,
    },
    {
      cpu: "16",
      memory: "64GB",
      storage: "1280GB",
      bandwidth: "10.00TB",
      monthlyPrice: 320.0,
    },
    {
      cpu: "24",
      memory: "96GB",
      storage: "1600GB",
      bandwidth: "15.00TB",
      monthlyPrice: 640.0,
    },
  ],
  footer: {
    btnText: "Get a Custom Quote",
  },
};
